import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";
import { Link } from "react-router-dom";
import { MapPin, Clock, Film, ArrowUpRight } from "lucide-react";
import { useContent } from "@/hooks/useContent";

const FILMS = [
  {
    date: "Oct 6, 2026",
    day: "Tue",
    title: "Lola Montez",
    year: "1955",
    director: "Max Ophuls",
    runtime: "1 hr 56 min",
  },
  {
    date: "Nov 3, 2026",
    day: "Tue",
    title: "2001: A Space Odyssey",
    year: "1968",
    director: "Stanley Kubrick",
    runtime: "2 hr 29 min",
  },
  {
    date: "Dec 1, 2026",
    day: "Tue",
    title: "The Shop Around the Corner",
    year: "1940",
    director: "Ernst Lubitsch",
    runtime: "1 hr 39 min",
  },
  {
    date: "Jan 5, 2027",
    day: "Tue",
    title: "Sunset Blvd.",
    year: "1950",
    director: "Billy Wilder",
    runtime: "1 hr 50 min",
  },
  {
    date: "Feb 2, 2027",
    day: "Tue",
    title: "Casablanca",
    year: "1942",
    director: "Michael Curtiz",
    runtime: "1 hr 42 min",
  },
  {
    date: "Mar 2, 2027",
    day: "Tue",
    title: "High Noon",
    year: "1952",
    director: "Fred Zinnemann",
    runtime: "1 hr 25 min",
  },
  {
    date: "Apr 6, 2027",
    day: "Tue",
    title: "The Big Lebowski",
    year: "1998",
    director: "Joel Coen & Ethan Coen",
    runtime: "1 hr 57 min",
  },
];

export default function FilmSeries() {
  // Admin-editable body. When populated, it replaces the hardcoded schedule
  // below (poster + hero stay). Empty by default -> fall back to the current
  // 2026-2027 film schedule.
  const { body: adminBody, loading } = useContent("filmseries.body", "");
  const hasAdminContent = !loading && !!(adminBody && adminBody.trim() && adminBody !== "<p></p>");

  return (
    <>
      <SEOHead
        title="2026-2027 Film Series - A Legacy of Jewish Directors"
        description="Temple Israel of Brevard's 2026-2027 Film Series celebrates a Legacy of Jewish Directors. Free screenings the first Tuesday of every month at 6:30 PM in the Social Hall - Lola Montez, 2001: A Space Odyssey, Casablanca, Sunset Blvd., The Big Lebowski and more."
        path="/calendar/film-series"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "EventSeries",
          name: "Temple Israel of Brevard Film Series 2026-2027",
          description: "A Legacy of Jewish Directors - a monthly film series screened the first Tuesday of every month at 6:30 PM in the Social Hall.",
          location: {
            "@type": "Place",
            name: "Temple Israel of Brevard, Social Hall",
            address: {
              "@type": "PostalAddress",
              streetAddress: "7300 Lake Andrew Drive",
              addressLocality: "Melbourne",
              addressRegion: "FL",
              postalCode: "32940",
              addressCountry: "US",
            },
          },
          subEvent: FILMS.map((f) => ({
            "@type": "ScreeningEvent",
            name: `${f.title} (${f.year})`,
            director: f.director,
            startDate: `${f.date} 18:30`,
            location: "Temple Israel of Brevard, Social Hall",
          })),
        }}
      />
      <PageHero
        eyebrow="Community Program"
        title="Film Series 2026-2027"
        subtitle="A Legacy of Jewish Directors - seven master filmmakers, seven unforgettable evenings. First Tuesday of every month, 6:30 PM, in the Social Hall."
      />

      <section className="max-w-6xl mx-auto px-5 lg:px-10 py-14 lg:py-20">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-start">
          {/* Left: schedule */}
          <div data-testid="film-series-schedule">
            {hasAdminContent ? (
              <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: adminBody }} />
            ) : (
              <>
            <p className="eyebrow mb-3">★ 2026-2027 Season</p>
            <h2 className="font-display text-3xl md:text-4xl text-[var(--blue-ink)] leading-tight">
              Seven films. Seven directors. One shared table.
            </h2>
            <p className="mt-5 text-[var(--ink-soft)] leading-relaxed">
              Join us on the <strong>first Tuesday of every month</strong> at{" "}
              <strong>6:30 PM</strong> for a curated screening celebrating the
              enduring influence of Jewish directors on the art of cinema.
              Discussion, popcorn, and warm company follow every film.
            </p>

            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <span className="inline-flex items-center gap-2 rounded-full bg-[var(--cream)] px-4 py-2 text-[var(--blue-deep)]">
                <Clock size={16} /> 6:30 PM start
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-[var(--cream)] px-4 py-2 text-[var(--blue-deep)]">
                <MapPin size={16} /> Social Hall - 7300 Lake Andrew Dr
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-[var(--honey)]/40 px-4 py-2 text-[var(--blue-ink)]">
                <Film size={16} /> Free & open to the community
              </span>
            </div>

            <ol className="mt-10 divide-y divide-[var(--line)] border-y border-[var(--line)]">
              {FILMS.map((f) => (
                <li
                  key={f.title}
                  data-testid={`film-row-${f.title.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                  className="grid grid-cols-[80px_1fr] md:grid-cols-[110px_1fr_auto] gap-4 md:gap-6 py-5 md:py-6 items-baseline"
                >
                  <div className="text-[var(--blue)] font-mono-accent text-xs uppercase tracking-widest">
                    <div>{f.date.split(",")[0]}</div>
                    <div className="text-[var(--muted)] text-[0.65rem] mt-1">
                      {f.day} · 6:30 PM
                    </div>
                  </div>
                  <div>
                    <div className="font-display text-xl md:text-2xl text-[var(--blue-ink)] leading-tight">
                      {f.title}{" "}
                      <span className="font-mono-accent text-sm text-[var(--muted)] tracking-normal">
                        ({f.year})
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-[var(--ink-soft)]">
                      Directed by <strong className="text-[var(--blue-deep)]">{f.director}</strong>{" "}
                      · {f.runtime}
                    </div>
                  </div>
                </li>
              ))}
            </ol>

              <div className="mt-10 rounded-2xl bg-[var(--cream)] p-6 md:p-7 border border-[var(--line)]">
              <h3 className="font-display text-xl text-[var(--blue-ink)]">Good to know</h3>
              <ul className="mt-3 space-y-2 text-sm text-[var(--ink-soft)]">
                <li>• Doors open at 6:00 PM - arrive early for a good seat and a chat.</li>
                <li>• Popcorn and light refreshments provided; feel free to bring a snack to share.</li>
                <li>• Members and non-members alike are welcome - bring a friend, invite a neighbor.</li>
                <li>• A short discussion follows each screening - always thoughtful, never mandatory.</li>
              </ul>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary" data-testid="film-series-contact-cta">
                  Ask a question <ArrowUpRight size={16} />
                </Link>
                <Link to="/calendar/month" className="btn-ghost">
                  See the full calendar
                </Link>
              </div>
            </div>
              </>
            )}
          </div>

          {/* Right: poster */}
          <div className="lg:sticky lg:top-24">
            <a
              href="/film-series-2026-2027.jpg"
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-3xl overflow-hidden border border-[var(--line)] shadow-lg hover:shadow-2xl transition-shadow"
              data-testid="film-series-poster-link"
            >
              <img
                src="/film-series-2026-2027.jpg"
                alt="Temple Israel of Brevard Film Series 2026-2027: A Legacy of Jewish Directors"
                className="w-full h-auto block"
                loading="lazy"
                data-testid="film-series-poster"
              />
            </a>
            <p className="mt-3 text-xs text-[var(--muted)] text-center">
              Tap the poster to view a larger version, or save it to share.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
